import { useEffect } from "react";

import { withAppBasePath } from "~/v2/utils/appBasePath";
import type { ViewState } from ".";

interface UseSyncSearchParamsArgs {
  symbol: string;
  isCrypto: boolean;
  view: ViewState;
  expiration: string | undefined;
}

export const useSyncSearchParams = ({
  symbol,
  isCrypto,
  view,
  expiration,
}: UseSyncSearchParamsArgs) => {
  // Hook to keep URL search parameters in sync
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Track if URL parameters have been updated
    let updated = false;

    // Update parameter based on a condition
    const updateParam = (
      name: string,
      next: string | undefined,
      condition: boolean = true,
    ) => {
      const current = params.get(name);

      if (next === undefined) {
        return;
      }

      // If condition met and values are different
      if (condition && current !== next) {
        params.set(name, next);
        updated = true;
      }
      // If condition not met but the parameter exists
      else if (!condition && current !== null) {
        params.delete(name);
        updated = true;
      }
    };

    updateParam("asset_class", view.tab.toLowerCase(), !isCrypto);

    // The `type` and `expiration` are only for options
    updateParam(
      "type",
      view.optionType === "Calls & Puts"
        ? "callsputs"
        : view.optionType.toLowerCase(),
      view.tab === "Options",
    );
    updateParam(
      "expiration",
      expiration,
      view.tab === "Options" && !!expiration,
    );

    if (updated && symbol) {
      // Replace the URL
      window.history.replaceState(
        {},
        "",
        withAppBasePath(`/trade/${symbol}?${params.toString()}`),
      );
    }
  }, [symbol, view, expiration, isCrypto]);
};
